import React, { useState } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';

// Choose the most empathic response to what someone says.
// Science: empathic accuracy and response selection predict relationship
// quality and perceived social competence (Ickes, 1993; Davis, 1983).

const SCENARIOS_EN = [
  {
    msg: '"I\'ve been working on this project for months and my boss barely acknowledged it."',
    answer: '"That must feel really discouraging after all that effort."',
    options: [
      '"That must feel really discouraging after all that effort."',
      '"You should tell your boss how you feel."',
      '"At least you finished it — that\'s what counts."',
      '"Maybe your boss is just busy."',
    ],
    why: 'Validates the emotion without minimising or advising.',
  },
  {
    msg: '"My friend cancelled on me again. It keeps happening."',
    answer: '"That pattern must really hurt, especially when you were looking forward to it."',
    options: [
      '"You should find better friends."',
      '"That pattern must really hurt, especially when you were looking forward to it."',
      '"People are just busy these days."',
      '"Have you talked to them about it?"',
    ],
    why: 'Reflects both the repeated hurt and the specific disappointment.',
  },
  {
    msg: '"I don\'t know if I made the right decision leaving that job."',
    answer: '"It sounds like you\'re still processing it — that uncertainty is hard to sit with."',
    options: [
      '"You made the right call — trust yourself."',
      '"What made you leave in the first place?"',
      '"It sounds like you\'re still processing it — that uncertainty is hard to sit with."',
      '"I\'m sure something better will come along."',
    ],
    why: 'Meets the uncertainty without resolving it prematurely.',
  },
  {
    msg: '"Nobody remembered my birthday this year."',
    answer: '"That really stings — being overlooked on that day especially."',
    options: [
      '"That really stings — being overlooked on that day especially."',
      '"You should remind people next time."',
      '"Birthdays aren\'t that important anyway."',
      '"I\'m sure they were just busy."',
    ],
    why: 'Names the specific pain without explaining it away.',
  },
  {
    msg: '"I feel like I\'m failing as a parent."',
    answer: '"The fact that you care so much shows how present you are — what\'s been feeling hardest?"',
    options: [
      '"You\'re not failing — all parents feel that way."',
      '"What specifically is going wrong?"',
      '"The fact that you care so much shows how present you are — what\'s been feeling hardest?"',
      '"Have you tried reading about parenting styles?"',
    ],
    why: 'Affirms without dismissing, then opens space to share more.',
  },
  {
    msg: '"I gave a speech today and I could feel myself shaking the whole time."',
    answer: '"That sounds nerve-racking — how do you feel it went overall?"',
    options: [
      '"Everyone gets nervous, it\'s totally normal."',
      '"Did people notice?"',
      '"That sounds nerve-racking — how do you feel it went overall?"',
      '"You need to practise more to build confidence."',
    ],
    why: 'Validates without trivialising, then invites their own reflection.',
  },
  {
    msg: '"My partner and I had a huge fight last night and I can\'t stop thinking about it."',
    answer: '"When something like that keeps replaying, it means it really got to you — do you want to talk through it?"',
    options: [
      '"Every couple fights — it\'ll blow over."',
      '"Who started it?"',
      '"When something like that keeps replaying, it means it really got to you — do you want to talk through it?"',
      '"You should apologise first to keep the peace."',
    ],
    why: 'Acknowledges the rumination and creates safe space.',
  },
  {
    msg: '"I got the job but I\'m honestly terrified I\'m not good enough."',
    answer: '"That mix of excitement and fear makes total sense — imposter feelings hit hardest at exactly this moment."',
    options: [
      '"You\'ll be fine — just fake it till you make it."',
      '"That mix of excitement and fear makes total sense — imposter feelings hit hardest at exactly this moment."',
      '"Why did you apply if you\'re not sure you\'re ready?"',
      '"Everyone feels that way, don\'t worry."',
    ],
    why: 'Holds both emotions and normalises without minimising.',
  },
];

const SCENARIOS_NL = [
  {
    msg: '"Ik heb maanden aan dit project gewerkt en mijn baas erkende het nauwelijks."',
    answer: '"Dat moet echt ontmoedigend voelen na al die moeite."',
    options: [
      '"Dat moet echt ontmoedigend voelen na al die moeite."',
      '"Je moet je baas vertellen hoe je je voelt."',
      '"Je hebt het in ieder geval afgemaakt — dat telt."',
      '"Misschien is je baas gewoon druk."',
    ],
    why: 'Valideert de emotie zonder te minimaliseren of advies te geven.',
  },
  {
    msg: '"Mijn vriend heeft me weer afgezegd. Het blijft maar gebeuren."',
    answer: '"Dat patroon moet echt pijn doen, zeker als je ernaar uitkeek."',
    options: [
      '"Je moet betere vrienden zoeken."',
      '"Dat patroon moet echt pijn doen, zeker als je ernaar uitkeek."',
      '"Mensen zijn gewoon druk tegenwoordig."',
      '"Heb je er met hen over gepraat?"',
    ],
    why: 'Weerspiegelt zowel de herhaalde pijn als de specifieke teleurstelling.',
  },
  {
    msg: '"Ik weet niet of ik de juiste beslissing heb genomen door die baan te verlaten."',
    answer: '"Het klinkt alsof je het nog aan het verwerken bent — die onzekerheid is moeilijk om mee te zitten."',
    options: [
      '"Je hebt de juiste keuze gemaakt — vertrouw jezelf."',
      '"Waarom ben je dan weggegaan?"',
      '"Het klinkt alsof je het nog aan het verwerken bent — die onzekerheid is moeilijk om mee te zitten."',
      '"Er komt vast iets beters."',
    ],
    why: 'Zit bij de onzekerheid zonder die te vroeg op te lossen.',
  },
  {
    msg: '"Niemand heeft dit jaar mijn verjaardag onthouden."',
    answer: '"Dat doet echt pijn — juist op die dag over het hoofd worden gezien."',
    options: [
      '"Dat doet echt pijn — juist op die dag over het hoofd worden gezien."',
      '"Herinner mensen er de volgende keer aan."',
      '"Verjaardagen zijn toch niet zo belangrijk."',
      '"Ze waren vast gewoon druk."',
    ],
    why: 'Benoemt de specifieke pijn zonder die weg te verklaren.',
  },
  {
    msg: '"Ik heb het gevoel dat ik faal als ouder."',
    answer: '"Het feit dat je er zo om geeft laat zien hoe aanwezig je bent — wat voelt het zwaarst?"',
    options: [
      '"Je faalt niet — alle ouders voelen dat zo."',
      '"Wat gaat er precies mis?"',
      '"Het feit dat je er zo om geeft laat zien hoe aanwezig je bent — wat voelt het zwaarst?"',
      '"Heb je gelezen over verschillende opvoedstijlen?"',
    ],
    why: 'Bevestigt zonder weg te wuiven, dan ruimte voor meer.',
  },
  {
    msg: '"Ik hield vandaag een speech en voelde mezelf de hele tijd trillen."',
    answer: '"Dat klinkt echt zenuwslopend — hoe vond je dat het ging?"',
    options: [
      '"Iedereen wordt nerveus, dat is heel normaal."',
      '"Merkte iemand het?"',
      '"Dat klinkt echt zenuwslopend — hoe vond je dat het ging?"',
      '"Je moet meer oefenen om zelfvertrouwen te bouwen."',
    ],
    why: 'Valideert zonder te bagatelliseren, nodigt dan hun eigen reflectie uit.',
  },
  {
    msg: '"Mijn partner en ik hadden gisteravond een grote ruzie en ik kan er niet mee stoppen."',
    answer: '"Als zoiets blijft naspelen, heeft het je echt geraakt — wil je erover praten?"',
    options: [
      '"Ieder stel heeft ruzies — het waait over."',
      '"Wie begon het?"',
      '"Als zoiets blijft naspelen, heeft het je echt geraakt — wil je erover praten?"',
      '"Bied als eerste je excuses aan om de vrede te bewaren."',
    ],
    why: 'Erkent het herkauwen en creëert veilige ruimte.',
  },
  {
    msg: '"Ik heb de baan gekregen maar ik ben eerlijk gezegd bang dat ik niet goed genoeg ben."',
    answer: '"Die mix van opwinding en angst is heel begrijpelijk — het imposter-gevoel slaat precies op dit moment het hardst toe."',
    options: [
      '"Het komt goed — doe maar alsof het zo is tot het echt zo is."',
      '"Die mix van opwinding en angst is heel begrijpelijk — het imposter-gevoel slaat precies op dit moment het hardst toe."',
      '"Waarom heb je gesolliciteerd als je twijfelt?"',
      '"Iedereen voelt dat zo, maak je geen zorgen."',
    ],
    why: 'Houdt beide emoties vast en normaliseert zonder te minimaliseren.',
  },
];

function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

function EmpathyMirrorGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const SCENARIOS = nl ? SCENARIOS_NL : SCENARIOS_EN;
  const ROUNDS = SCENARIOS.length;

  const [phase, setPhase]   = useState('idle');
  const [round, setRound]   = useState(0);
  const [score, setScore]   = useState(0);
  const [picked, setPicked] = useState(null);
  const [shuffled, setShuffled] = useState([]);
  const [results, setResults]   = useState([]);
  const [showWhy, setShowWhy]   = useState(false);
  const [best, setBest] = useStorage('gm_empathymirror_best', 0);

  const current = SCENARIOS[round];

  const startGame = () => {
    setPhase('playing'); setRound(0); setScore(0);
    setPicked(null); setResults([]); setShowWhy(false);
    setShuffled(shuffle(SCENARIOS[0].options));
  };

  const pick = (option) => {
    if (picked !== null) return;
    setPicked(option);
    const correct = option === current.answer;
    const newScore = score + (correct ? 1 : 0);
    const newResults = [...results, { correct }];
    setScore(newScore);
    setResults(newResults);
    setShowWhy(true);

    setTimeout(() => {
      setShowWhy(false);
      if (round + 1 >= ROUNDS) {
        if (newScore > best) setBest(newScore);
        saveGameScore('empathymirror', newScore);
        setPhase('done');
      } else {
        const next = round + 1;
        setRound(next);
        setPicked(null);
        setShuffled(shuffle(SCENARIOS[next].options));
      }
    }, 1800);
  };

  const col = '#7B6FD0';

  if (phase === 'idle') return (
    <div style={{ display:'flex',flexDirection:'column',gap:16,paddingTop:8 }}>
      <div style={s_GC({ padding:'20px 18px' })}>
        <div style={{ fontSize:9,color:col,letterSpacing:3,fontWeight:700,marginBottom:6 }}>
          {nl ? 'HOE HET WERKT' : 'HOW IT WORKS'}
        </div>
        <div style={{ fontSize:14,color:C.white,fontWeight:700,marginBottom:8 }}>
          {nl ? 'Lees wat iemand zegt. Kies de meest empathische reactie.' : 'Read what someone says. Choose the most empathic response.'}
        </div>
        <div style={{ fontSize:12,color:C.silver,lineHeight:1.65,marginBottom:4 }}>
          {nl
            ? `${ROUNDS} situaties · Vier opties · Leer empathisch reageren`
            : `${ROUNDS} situations · Four options · Learn to respond with empathy`}
        </div>
        <div style={{ fontSize:10,color:`${col}80`,fontStyle:'italic',marginTop:8 }}>
          {nl
            ? 'Bron: Ickes (1993); Davis (1983) — empathische nauwkeurigheid voorspelt relatie­kwaliteit.'
            : 'Source: Ickes (1993); Davis (1983) — empathic accuracy predicts relationship quality.'}
        </div>
      </div>
      <button style={s_PB({ width:'100%' })} onClick={startGame}>
        {nl ? '▶ Start Empathiespiegel' : '▶ Start Empathy Mirror'}
      </button>
    </div>
  );

  if (phase === 'done') return (
    <div style={{ display:'flex',flexDirection:'column',gap:12,paddingTop:8 }}>
      <div style={s_GC({ padding:'20px 18px', textAlign:'center' })}>
        <div style={{ fontSize:36,marginBottom:8 }}>
          {score >= 6 ? '🥇' : score >= 4 ? '🥈' : '🥉'}
        </div>
        <div style={{ fontSize:28,fontWeight:900,color:col,fontFamily:'Georgia,serif' }}>{score}/{ROUNDS}</div>
        <div style={{ fontSize:13,color:C.white,fontWeight:700,marginTop:4,marginBottom:8 }}>
          {score >= 6 ? (nl?'Uitstekend empathisch reageren!':'Excellent empathic responding!') :
           score >= 4 ? (nl?'Goed — je voelt mensen aan.':'Good — you tune in to people.') :
           (nl?'Blijf oefenen — empathie groeit.':'Keep practising — empathy grows.')}
        </div>
        {best > 0 && <div style={{ fontSize:11,color:C.silver }}>
          {nl?`Beste: ${best}/${ROUNDS}`:`Best: ${best}/${ROUNDS}`}
        </div>}
        <div style={{ display:'flex',justifyContent:'center',gap:6,marginTop:12,flexWrap:'wrap' }}>
          {results.map((r,i) => (
            <div key={i} style={{ width:24,height:24,borderRadius:'50%',background:r.correct?'#22c55e':'#ef4444',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12 }}>
              {r.correct?'✓':'✗'}
            </div>
          ))}
        </div>
      </div>
      <button style={s_PB({ width:'100%' })} onClick={startGame}>{nl?'Opnieuw':'Play again'}</button>
      <button onClick={onBack} style={{ background:'none',border:'none',color:C.silver,cursor:'pointer',fontSize:13,padding:8 }}>
        ← {nl?'Terug':'Back'}
      </button>
    </div>
  );

  // Playing
  return (
    <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
      {/* Progress */}
      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
        <div style={{ flex:1,height:4,background:'rgba(255,255,255,0.08)',borderRadius:4,overflow:'hidden' }}>
          <div style={{ height:'100%',width:`${(round/ROUNDS)*100}%`,background:`linear-gradient(90deg,${col},${col}aa)`,transition:'width 0.3s' }}/>
        </div>
        <div style={{ fontSize:11,color:C.silver,flexShrink:0 }}>{round+1}/{ROUNDS}</div>
        <div style={{ fontSize:11,color:col,fontWeight:700,flexShrink:0 }}>{score} ✓</div>
      </div>

      {/* Message card */}
      <div style={{ background:_T.bg3,border:`1px solid ${_T.cardBorderTop}`,borderRadius:16,padding:'20px 18px' }}>
        <div style={{ fontSize:9,color:col,letterSpacing:3,fontWeight:700,marginBottom:10 }}>
          {nl ? 'WAT ZEGT DEZE PERSOON?' : 'WHAT DOES THIS PERSON SAY?'}
        </div>
        <div style={{ fontSize:15,color:C.white,lineHeight:1.65,fontStyle:'italic',fontFamily:'Georgia,serif' }}>
          {current.msg}
        </div>
      </div>

      {/* Why hint after pick */}
      {showWhy && picked && (
        <div style={{ background: picked === current.answer ? '#22c55e12' : '#ef444412', border: `1px solid ${picked === current.answer ? '#22c55e44' : '#ef444444'}`, borderRadius:12, padding:'10px 14px' }}>
          <div style={{ fontSize:11,color:picked === current.answer ? '#22c55e' : '#ef4444',fontWeight:700,marginBottom:4 }}>
            {picked === current.answer ? (nl?'✓ Meest empathisch':'✓ Most empathic') : (nl?'✗ Minder empathisch':'✗ Less empathic')}
          </div>
          <div style={{ fontSize:11,color:C.silver,lineHeight:1.5 }}>
            {current.why}
          </div>
        </div>
      )}

      {/* Options */}
      <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
        {shuffled.map(opt => {
          const isCorrect = opt === current.answer;
          const isPicked  = opt === picked;
          let bg = _T.bg3, border = `1px solid ${_T.cardBorderBot}`, textCol = C.white;
          if (picked !== null) {
            if (isCorrect)     { bg = '#22c55e18'; border = '1px solid #22c55e88'; textCol = '#22c55e'; }
            else if (isPicked) { bg = '#ef444418'; border = '1px solid #ef444488'; textCol = '#ef4444'; }
          }
          return (
            <button key={opt} onClick={() => pick(opt)} style={{
              background:bg, border, borderRadius:12,
              padding:'12px 14px', cursor: picked ? 'default' : 'pointer',
              color:textCol, fontSize:13, fontWeight:500, textAlign:'left',
              transition:'all 0.25s', lineHeight:1.5,
            }}>
              {isPicked && picked !== null ? (isCorrect ? '✓ ' : '✗ ') : ''}{opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default EmpathyMirrorGame;
